import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { UserPlus, RotateCw } from "lucide-react";
import OnboardDialog from "@/components/OnboardDialog";

const LABEL = { account: "Site account", affiliate: "Affiliate", crm_main: "CRM (main)", crm_sub: "Own sub-account", cap: "Credit cap", email: "Welcome email", alert: "Text to you" };

const Step = ({ p, k, onRetry }) => {
  const s = p.steps?.[k];
  const cls = !s ? "bg-gray-100 text-gray-500" : s.skipped ? "bg-gray-100 text-gray-600" : s.ok ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800";
  return (
    <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] ${cls}`} title={s?.error || s?.note || ""} data-testid={`onb-step-${k}-${p.id}`}>
      {LABEL[k]}{s?.skipped ? " (n/a)" : ""}
      {s && !s.ok && <button onClick={() => onRetry(p, k)} className="underline" data-testid={`onb-retry-${k}-${p.id}`}>Retry</button>}
    </span>
  );
};

export default function OnboardingAdmin() {
  const [d, setD] = useState({ people: [], steps: [], caps: {} });
  const [open, setOpen] = useState(false);
  const [caps, setCaps] = useState({});
  const load = useCallback(() => api.get("/admin/onboarding").then(({ data }) => { setD(data); setCaps(data.caps); }).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const retry = async (p, k) => {
    try { const { data } = await api.post(`/admin/onboarding/${p.id}/retry/${k}`); data.steps[k]?.ok ? toast.success(`${LABEL[k]} done`) : toast.error(data.steps[k]?.error || "Still failing"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Retry failed"); }
  };
  const saveCaps = async () => {
    try { await api.put("/admin/onboarding/caps", caps); toast.success("Credit caps saved"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't save caps"); }
  };
  return (
    <div className="mb-6 rounded-lg border p-4 space-y-3" data-testid="onboarding-admin">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <h3 className="font-semibold">One-step onboarding</h3>
        <Button size="sm" onClick={() => setOpen(true)} data-testid="onboard-add-btn"><UserPlus className="h-4 w-4 mr-1" />Add person</Button>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-xs text-sam-slate">
        Monthly credit caps: rep <Input type="number" min="0" className="h-7 w-20" value={caps.rep ?? ""} onChange={(e) => setCaps({ ...caps, rep: e.target.value })} data-testid="onb-cap-rep" />
        owner <Input type="number" min="0" className="h-7 w-20" value={caps.owner ?? ""} onChange={(e) => setCaps({ ...caps, owner: e.target.value })} data-testid="onb-cap-owner" />
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={saveCaps} data-testid="onb-cap-save">Save</Button>
      </div>
      {d.people.length === 0 && <p className="text-xs text-sam-slate">No one onboarded yet.</p>}
      {d.people.map((p) => (
        <div key={p.id} className="border-t pt-2 space-y-1" data-testid={`onb-row-${p.id}`}>
          <div className="text-sm"><b>{p.name}</b> <span className="text-sam-slate">{p.email} · {p.kind}{p.referral_code ? ` · ref ${p.referral_code}` : ""}{p.zuops_workspace_id ? " · own sub-account" : ""}{p.credits_used_month != null ? ` · ${p.credits_used_month}/${p.credit_cap} credits` : ""}</span>
            <button className="ml-2 text-xs underline inline-flex items-center" onClick={() => retry(p, "email")} data-testid={`onb-resend-${p.id}`}><RotateCw className="h-3 w-3 mr-0.5" />Resend email</button>
          </div>
          <div className="flex flex-wrap gap-1">{d.steps.map((k) => <Step key={k} p={p} k={k} onRetry={retry} />)}</div>
        </div>
      ))}
      {open && <OnboardDialog open={open} onOpenChange={setOpen} onDone={load} />}
    </div>
  );
}
